import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Card, 
  Typography, 
  Button, 
  Tag, 
  Tooltip,
  Input,
  Select,
  Badge,
  Modal,
  message
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { getBlogs, deleteBlog, setFilters, clearError, incrementBlogViews } from '../../features/blog/blogSlice';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { confirm } = Modal;

const RenderBlogs = ({ onEdit }) => {
  const dispatch = useDispatch();
  const { blogs, loading, pagination, filters, deleting, error } = useSelector((state) => state.blogs);
  const { user } = useSelector((state) => state.auth); // Get user info for role checking
  
  const [searchTerm, setSearchTerm] = useState(filters.search || '');
  const [selectedCategory, setSelectedCategory] = useState(filters.category || 'all');

  // Check if user is admin (can simulate views)
  const isAdmin = user?.role === 'admin' || user?.role === 'omega' || user?.role === 'root';

  // Fetch blogs on component mount and when filters change
  useEffect(() => {
    const fetchParams = {
      page: pagination.currentPage || 1,
      limit: 12,
      ...(filters.status !== 'all' && { status: filters.status }),
      ...(filters.category !== 'all' && { category: filters.category }),
      ...(filters.search && { search: filters.search }),
      sortBy: filters.sortBy || 'createdAt',
      sortOrder: filters.sortOrder || 'desc'
    };
    
    dispatch(getBlogs(fetchParams));
  }, [dispatch, filters, pagination.currentPage]);

  // Handle search input change with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      dispatch(setFilters({ search: searchTerm }));
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, dispatch]);

  // Handle category filter change
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    dispatch(setFilters({ category }));
  };

  // Handle blog deletion with confirmation
  const handleDelete = (blog) => {
    confirm({
      title: 'Delete Blog Post',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Are you sure you want to delete this blog post?</p>
          <p><strong>"{blog.title}"</strong></p>
          <p className="text-red-600">This action cannot be undone.</p>
        </div>
      ),
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        dispatch(deleteBlog(blog._id))
          .unwrap()
          .then(() => {
            message.success('Blog post deleted successfully');
          })
          .catch((err) => {
            message.error(err || 'Failed to delete blog post');
          });
      },
    });
  };

  // Handle pagination change
  const handlePageChange = (page) => {
    dispatch(setFilters({ page }));
  };

  // Handle view increment - simulate a "view" when clicking the eye icon
  const handleViewClick = (blog) => {
    if (blog.status === 'published') {
      dispatch(incrementBlogViews(blog._id))
        .unwrap()
        .then(() => {
          message.success(`View recorded for "${blog.title}"`);
        })
        .catch((error) => {
          message.error(error || 'Failed to record view');
        });
    } else {
      message.info('Only published blogs can be viewed');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Not published';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get category color
  const getCategoryColor = (category) => {
    const colors = {
      technology: 'blue',
      design: 'purple',
      development: 'green',
      career: 'orange',
      tutorial: 'cyan',
      personal: 'pink'
    };
    return colors[category] || 'default';
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      published: 'green',
      draft: 'orange',
      archived: 'gray'
    };
    return colors[status] || 'default';
  };

  // Show error message if any
  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  return (
    <div className="max-w-7xl mx-auto p-6 bg-bg-primary">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-4">
          <FileTextOutlined className="text-2xl text-accent-blue" />
          <Title level={2} className="m-0 text-text-primary font-semibold">
            My Blog Posts
          </Title>
        </div>
        <Text className="text-text-secondary text-base">
          Manage and view all your blog posts
        </Text>
      </div>

      {/* Search and Filter Section */}
      <Card className="shadow-soft border border-border-light rounded-xl bg-white mb-8">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex-1 max-w-md">
            <Search
              placeholder="Search blogs by title, content, or tags..."
              allowClear
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
              size="large"
              loading={loading}
            />
          </div>
          <div className="flex items-center space-x-4">
            <Select
              value={selectedCategory}
              onChange={handleCategoryChange}
              className="w-48"
              size="large"
            >
              <Select.Option value="all">All Categories</Select.Option>
              <Select.Option value="technology">💻 Technology</Select.Option>
              <Select.Option value="design">🎨 Design</Select.Option>
              <Select.Option value="development">⚡ Development</Select.Option>
              <Select.Option value="career">🚀 Career</Select.Option>
              <Select.Option value="tutorial">📚 Tutorial</Select.Option>
              <Select.Option value="personal">👤 Personal</Select.Option>
            </Select>
            
            <Select
              value={filters.status}
              onChange={(status) => dispatch(setFilters({ status }))}
              className="w-32"
              size="large"
            >
              <Select.Option value="all">All Status</Select.Option>
              <Select.Option value="published">Published</Select.Option>
              <Select.Option value="draft">Draft</Select.Option>
              <Select.Option value="archived">Archived</Select.Option>
            </Select>

            <Badge count={blogs.length} style={{ backgroundColor: '#19868d' }}>
              <Text className="text-text-secondary">Posts</Text>
            </Badge>
          </div>
        </div>
      </Card>

      {/* Blog Posts Grid */}
      {blogs.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogs.map((blog) => (
              <Card
                key={blog._id}
                className="shadow-medium border border-border-light rounded-xl hover:shadow-hard transition-all duration-300 bg-white h-full flex flex-col"
                bodyStyle={{ padding: '24px', display: 'flex', flexDirection: 'column', height: '100%' }}
                loading={deleting && deleting === blog._id}
              >
                {/* Blog Header */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Tag color={getCategoryColor(blog.category)} className="mb-2">
                        {blog.category.toUpperCase()}
                      </Tag>
                      <Tag color={getStatusColor(blog.status)} className="mb-2">
                        {blog.status.toUpperCase()}
                      </Tag>
                    </div>
                    <div className="flex items-center space-x-1 text-text-secondary text-xs">
                      {isAdmin && blog.status === 'published' ? (
                        // Admin: Clickable increment button
                        <Tooltip title="Simulate a view">
                          <Button
                            type="text"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => handleViewClick(blog)}
                            className="p-1 hover:text-accent-blue cursor-pointer"
                          />
                        </Tooltip>
                      ) : (
                        // Non-admin or non-published: Just display icon
                        <Tooltip title={blog.status !== 'published' ? "Only published blogs can be viewed" : "View count"}>
                          <EyeOutlined className={blog.status !== 'published' ? 'opacity-50' : ''} />
                        </Tooltip>
                      )}
                      <span className="font-medium">{blog.views || 0}</span>
                    </div>
                  </div>

                  <Title level={4} className="text-text-primary mb-2 line-clamp-2 hover:text-accent-blue transition-colors cursor-pointer">
                    {blog.title}
                  </Title>

                  <Paragraph 
                    className="text-text-secondary text-sm mb-4 line-clamp-3"
                    ellipsis={{ rows: 3, expandable: false }}
                  >
                    {blog.summary}
                  </Paragraph>

                  {/* Tags */}
                  {blog.tags && blog.tags.length > 0 && (
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-1">
                        {blog.tags.slice(0, 3).map((tag, index) => (
                          <Tag key={index} size="small" className="text-xs">
                            {tag}
                          </Tag>
                        ))}
                        {blog.tags.length > 3 && (
                          <Tag size="small" className="text-xs">
                            +{blog.tags.length - 3} more
                          </Tag>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Blog Footer */}
                <div className="mt-auto pt-4 border-t border-border-light">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-4 text-xs text-text-secondary">
                      <div className="flex items-center space-x-1">
                        <CalendarOutlined />
                        <span>
                          {blog.status === 'published' 
                            ? formatDate(blog.publishedAt)
                            : `Created ${formatDate(blog.createdAt)}`
                          }
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <ClockCircleOutlined />
                        <span>{blog.readTime} min read</span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-end space-x-2">
                    <Tooltip title="Edit Blog">
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => onEdit && onEdit(blog)}
                        className="text-accent-blue hover:text-accent-blue/80 hover:bg-accent-blue/10"
                      >
                        Edit
                      </Button>
                    </Tooltip>
                    <Tooltip title="Delete Blog">
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(blog)}
                        className="text-accent-red hover:text-accent-red/80 hover:bg-accent-red/10"
                        danger
                      >
                        Delete
                      </Button>
                    </Tooltip>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex justify-center mt-8">
              <Button.Group>
                <Button 
                  disabled={pagination.currentPage === 1}
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                >
                  Previous
                </Button>
                <Button disabled>
                  Page {pagination.currentPage} of {pagination.totalPages}
                </Button>
                <Button 
                  disabled={pagination.currentPage === pagination.totalPages}
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                >
                  Next
                </Button>
              </Button.Group>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12">
          <FileTextOutlined className="text-6xl text-neutral-400 mb-4" />
          <Title level={3} className="text-neutral-500 mb-2">
            {loading ? 'Loading blogs...' : 
             (searchTerm || selectedCategory !== 'all' || filters.status !== 'all') ? 'No blogs found' : 'No blog posts yet'}
          </Title>
          <Text className="text-neutral-400">
            {loading ? 'Please wait while we fetch your blog posts' :
             (searchTerm || selectedCategory !== 'all' || filters.status !== 'all') 
              ? 'Try adjusting your search or filter criteria'
              : 'Start creating your first blog post to share your thoughts and expertise'
            }
          </Text>
        </div>
      )}
    </div>
  );
};

export default RenderBlogs;